import { useEffect, useState } from "react";
import { Trans, useLingui } from "@lingui/react/macro";
import { Download, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  ProfileTable,
  Td,
  Th,
  TablePaginationBar,
} from "@/components/profile/profile-table";
import { TableBody, TableHeader, TableRow } from "@/components/ui/table";
import { useActivityLog } from "@/hooks/useActivityLog";
import type { ActivityLogParams } from "@/lib/api/services/activity.api";
import {
  activityActionLabel,
  activityEntityLabel,
  activityRowsToCsv,
  activityWhen,
} from "./activity-format";

const PAGE_SIZE = 10;

type ScopeParams = Pick<ActivityLogParams, "entityType" | "entityId" | "userId">;

/**
 * Self-contained activity-log table: search + server pagination + CSV download.
 * `scope` narrows the query by entity or user; omit it for the viewer's own
 * (or, with `audit:read`, org-wide) activity.
 */
export function ActivityLogTable({
  scope,
  showActor = false,
}: {
  scope?: ScopeParams;
  showActor?: boolean;
}) {
  const { t, i18n } = useLingui();
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, isError } = useActivityLog({
    ...scope,
    page,
    limit: PAGE_SIZE,
    search: search || undefined,
  });

  const rows = data?.data ?? [];
  const meta = data?.meta;

  const handleDownload = () => {
    const blob = new Blob([activityRowsToCsv(rows, i18n)], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `activity-log-page-${page}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const emptyMessage = isLoading
    ? t`Loading…`
    : isError
      ? t`Couldn't load activity.`
      : t`No activity yet.`;

  const columnCount = showActor ? 5 : 4;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-(--color-text-secondary)" />
          <input
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            className="h-10 w-full rounded-lg border border-(--color-border) bg-(--color-surface-content) pl-9 pr-3 text-sm text-(--color-text-primary) outline-none placeholder:text-(--color-text-secondary) sm:w-64"
            placeholder={t`Search action or entity…`}
          />
        </div>
        <Button onClick={handleDownload} disabled={rows.length === 0}>
          <Download />
          <Trans>Download</Trans>
        </Button>
      </div>

      <ProfileTable>
        <TableHeader>
          <TableRow>
            <Th><Trans>Action</Trans></Th>
            <Th><Trans>Entity</Trans></Th>
            {showActor ? <Th><Trans>Actor</Trans></Th> : null}
            <Th sorted><Trans>When</Trans></Th>
            <Th><Trans>IP</Trans></Th>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id}>
              <Td className="font-medium">{activityActionLabel(row, i18n)}</Td>
              <Td className="text-(--color-text-secondary)">
                {activityEntityLabel(row)}
              </Td>
              {showActor ? (
                <Td className="text-(--color-text-secondary)">
                  {row.actor?.name ?? "—"}
                </Td>
              ) : null}
              <Td className="text-(--color-text-secondary)">
                {activityWhen(row.createdAt)}
              </Td>
              <Td className="text-(--color-text-secondary)">
                {row.ipAddress ?? "—"}
              </Td>
            </TableRow>
          ))}
          {rows.length === 0 ? (
            <TableRow>
              <Td className="text-(--color-text-secondary)">{emptyMessage}</Td>
              {Array.from({ length: columnCount - 1 }).map((_, i) => (
                <Td key={i}>{null}</Td>
              ))}
            </TableRow>
          ) : null}
        </TableBody>
      </ProfileTable>

      <TablePaginationBar
        shown={rows.length}
        total={meta?.total ?? 0}
        page={meta?.page ?? page}
        pageCount={meta?.totalPages ?? 1}
        rowsPerPage={PAGE_SIZE}
        onPageChange={setPage}
      />
    </div>
  );
}
